import { createContext, useContext, useState, ReactNode, useRef, useEffect } from 'react';
import { PlickNotification } from '@interfaces/general';
import { usePreferencesFromStore } from '@store/actions/preferences';
import { shouldStopSharedAlarm } from '@utils/notificationSound';
import { reportAlarmBlockedWithoutUnlock, reportUnplayableAlarmSource, safePlay } from '@utils/safePlay';

interface NotificationContextProps {
  notifications: PlickNotification[];
  addNotification: (notification: PlickNotification) => void;
  removeNotification: (id: string, stopNotificationsSound?: boolean) => void;
}

const NotificationContext = createContext<NotificationContextProps>({
  notifications: [],
  addNotification: () => null,
  removeNotification: () => null,
});

/**
 * Whether a real alarm is live on the shared <audio> RIGHT NOW.
 *
 * `loop` is the only synchronous marker of that: it is set exclusively by playSound(), cleared
 * exclusively by stopSound(), and merely carried over by the resync effect — the autoplay priming
 * never touches it. `paused` cannot answer the question, because reloading a source pauses a live
 * alarm and a muted priming play() unpauses a stopped one.
 *
 * It exists because two places decide "should this ring?" at one moment and act hundreds of ms
 * later (the priming callback and the `canplay` handler); both must re-ask at the moment they act.
 *
 * @param {HTMLAudioElement} audio shared alarm element
 * @returns {boolean} true while an order alarm is still supposed to be ringing
 */
const isAlarmLive = (audio: HTMLAudioElement): boolean => audio.loop;

export const useNotificationContext = () => {
  return useContext(NotificationContext);
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<PlickNotification[]>([]);
  const { audio: storedAudio } = usePreferencesFromStore();
  const sound = useRef(new Audio(storedAudio));
  // IDs de notificaciones activas (ref, no state): fuente de verdad SÍNCRONA para el dedup de
  // addNotification. `setNotifications` es asíncrono (el updater se aplica en el commit, no en el
  // momento de la llamada), así que leer `notifications` o una bandera seteada dentro del updater
  // para decidir si sonar/timerear sería una carrera. El ref se actualiza en el mismo tick que se
  // agrega/quita la notificación, así que dos entregas del mismo evento (mismo id determinista,
  // p. ej. `${orderId}_scheduled_created`) SIEMPRE ven el estado correcto.
  const activeNotificationIdsRef = useRef<Set<string>>(new Set());
  // Autoplay state of this provider: a successful play() (primed or real) proves playback is
  // unlocked. The provider is mounted by the root layout route, so this lives as long as the panel
  // session does — only a logout/login round-trip resets it.
  const autoplayUnlockedRef = useRef(false);
  const autoplayLockReportedRef = useRef(false);

  const playSound = () => {
    const audio = sound.current;
    audio.loop = true;
    // safePlay instead of a bare `audio.play()`: the unhandled promise WAS the whole of
    // PLICK-FRONT-PANEL-COMERCIO-173/-127 (550 events), which reached Sentry as unhandled rejections.
    safePlay(audio, (error) => {
      if (!error) {
        autoplayUnlockedRef.current = true;
        return;
      }
      // Keep ONE bounded signal for the population the breadcrumb loses: a panel on a wall-mounted
      // tablet nobody ever touches never produces the gesture the unlock effect waits for, so the
      // alarm stays mute the whole session, no later error carries the breadcrumb out, and the
      // commerce never finds out. One warning per affected panel session, not one per order.
      if (!autoplayUnlockedRef.current && !autoplayLockReportedRef.current) {
        autoplayLockReportedRef.current = reportAlarmBlockedWithoutUnlock(error);
      }
    });
  };

  const stopSound = () => {
    const audio = sound.current;
    audio.loop = false;
    audio.pause();
    audio.currentTime = 0;
  };

  // Re-sincroniza el reproductor cuando el usuario cambia el sonido en Ajustes.
  // Sin esto, useRef conserva el Audio del primer render (el sonido que había al
  // cargar la página) y el sonido elegido no se aplica hasta recargar.
  useEffect(() => {
    const a = sound.current;
    if (!a) return;
    // Si hay una alarma sonando al cambiar el sonido, conserva la reproducción
    // con el sonido nuevo en vez de cortarla.
    const wasLooping = a.loop;
    a.src = storedAudio;
    if (!wasLooping) return;
    a.loop = true;
    // Wait for `canplay` instead of playing right away: a play() issued while the new source is
    // still loading gets aborted by the next load request, and that AbortError is indistinguishable
    // BY NAME from the benign one stopSound() produces when it pauses. Only the message text tells
    // them apart, and classifying by browser message is exactly what already failed in `ignoreErrors`.
    const onCanPlay = () => {
      // Re-check when the source is READY, not when the listener was registered: the operator can
      // accept or ignore the order while the new tone is still downloading (stopSound clears
      // `loop`), and playing here would ring a full pass after they already silenced it.
      if (isAlarmLive(a)) safePlay(a);
    };
    // The canary lost by not playing blind: a source that cannot load no longer rejects with
    // NotSupportedError, so the media `error` event takes its place.
    const onError = () => reportUnplayableAlarmSource(a);
    a.addEventListener('canplay', onCanPlay, { once: true });
    a.addEventListener('error', onError, { once: true });
    return () => {
      a.removeEventListener('canplay', onCanPlay);
      a.removeEventListener('error', onError);
    };
  }, [storedAudio]);

  useEffect(() => {
    return () => {
      if (sound.current) stopSound();
    };
  }, []);

  // Desbloqueo de autoplay: los navegadores bloquean `audio.play()` programático hasta que el
  // usuario interactúa con la página. Sin esto, una alarma que se dispara mientras el comercio
  // solo OBSERVA el panel (p. ej. un pedido programado creado desde otro dispositivo) queda
  // silenciada. Al primer gesto, "primamos" el Audio compartido (play mudo + pause) para
  // habilitar reproducciones futuras. Beneficia a TODAS las alarmas (entrantes y programados).
  useEffect(() => {
    // AbortController to unregister: the handler and its own removal need each other, and
    // `no-use-before-define` is an error in this repo, so the signal breaks the cycle.
    const controller = new AbortController();
    let priming = false;
    const unlock = () => {
      const a = sound.current;
      if (!a || priming) return;
      // Already playing means autoplay is already unlocked, and priming here would PAUSE a live
      // alarm: Electron's default policy lets the alarm start with no gesture (32 of the events on
      // PLICK-FRONT-PANEL-COMERCIO-64 are Electron), so the operator's first click used to silence
      // an alarm that was mid-ring.
      if (!a.paused) {
        autoplayUnlockedRef.current = true;
        controller.abort();
        return;
      }
      priming = true;
      const prevMuted = a.muted;
      a.muted = true;
      safePlay(a, (error) => {
        priming = false;
        a.muted = prevMuted;
        // Unregister ONLY once the priming is confirmed. The listeners used to be removed
        // synchronously, without awaiting the promise: on iOS, where a gesture does not always
        // count, the commerce lost the alarm for the rest of the session while still using the panel.
        if (error) return;
        autoplayUnlockedRef.current = true;
        // Re-check AFTER restoring `muted`: an order can arrive while the muted play() is still in
        // flight (on iOS the media is not even downloaded until the gesture, so that window is
        // hundreds of ms). Pausing here would kill an alarm the operator never got to hear — the
        // very case the entry guard above closes, but only for gestures that arrive first.
        if (isAlarmLive(a)) {
          controller.abort();
          return;
        }
        a.pause();
        a.currentTime = 0;
        controller.abort();
      });
    };
    window.addEventListener('pointerdown', unlock, { signal: controller.signal });
    window.addEventListener('keydown', unlock, { signal: controller.signal });
    return () => controller.abort();
  }, []);

  const removeNotification = (id: string, stopNotificationsSound = false) => {
    // Libera el id para permitir una re-notificación legítima futura del mismo pedido.
    activeNotificationIdsRef.current.delete(id);
    setNotifications((prev) => {
      const remaining = prev.filter((item) => item.id !== id);
      // Silenciado colateral (Audio único compartido): detener la alarma solo si NINGUNA
      // notificación restante sigue sonando. Así "Ignorar" en una notificación no apaga la
      // alarma de otra que aún debe sonar (p. ej. ignorar un pedido de otra sucursal mientras
      // suena uno propio).
      if (stopNotificationsSound && shouldStopSharedAlarm(remaining)) stopSound();
      return remaining;
    });
  };

  const addNotification = (notification: PlickNotification) => {
    // Dedup por id (mismo patrón que scheduledOrdersSlice.addScheduledOrder): si la notificación
    // ya está activa (no se ha removido vía accept/reject/autoclose), una segunda entrega del
    // mismo evento (p. ej. `scheduledOrderCreated` duplicado por el socket) no debe re-agregar el
    // toast ni volver a sonar la alarma. Una vez removida, un id reutilizado vuelve a admitirse.
    if (activeNotificationIdsRef.current.has(notification.id)) return;
    activeNotificationIdsRef.current.add(notification.id);

    setNotifications((prev) => [...prev, notification]);
    if (!notification?.mute) playSound();

    if (notification?.autoCloseTimer) {
      setTimeout(() => {
        removeNotification(notification.id, !notification?.mute);
      }, notification.autoCloseTimer - 250);
    }
  };

  const notificationValues: NotificationContextProps = {
    notifications,
    addNotification,
    removeNotification,
  };

  return <NotificationContext.Provider value={notificationValues}>{children}</NotificationContext.Provider>;
};
